import { motion } from 'framer-motion'
import { ChevronLeft } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/utils/cn'

export interface SidebarOpenButtonProps {
  onClick: () => void
  className?: string
  label?: string
}

/**
 * Single source of truth for the "open sidebar" floating button used across
 * Map / Graph / Timeline. Visual matches the legacy MapToolbar's open-sidebar
 * button so all main views share the same affordance.
 */
export function SidebarOpenButton({
  onClick,
  className,
  label = 'Open sidebar',
}: SidebarOpenButtonProps) {
  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>
          <motion.button
            type="button"
            onClick={onClick}
            initial={{ opacity: 0, scale: 0.85, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: -8 }}
            transition={{ type: 'spring', stiffness: 380, damping: 32, mass: 0.9 }}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            className={cn(
              'inline-flex h-10 w-10 items-center justify-center rounded-xl',
              'border border-primary/55 bg-primary/90 text-primary-foreground',
              'backdrop-blur-xl backdrop-saturate-150',
              'shadow-[0_0_0_1px_hsl(var(--primary)/0.3),0_12px_30px_-8px_hsl(var(--primary)/0.45),inset_0_1px_0_hsl(0_0%_100%/0.15)]',
              'transition-colors hover:bg-primary',
              className
            )}
            aria-label={label}
          >
            <ChevronLeft className="size-4" />
          </motion.button>
        </TooltipTrigger>
        <TooltipContent side="left">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
